import { useState } from 'react';
import { Box, Button, Dot, Link, Skeleton, SkeletonBox, Text } from '@bitrise/bitkit';
import useLatestActivities from '@/hooks/api/useLatestActivities';
import { formatDateTime } from '@/utils/commonFormats';

export const getActivityColor = (title: string) => {
  if (title.includes('failed')) {
    return 'red.60';
  }

  if (title.includes('finished')) {
    return 'green.60';
  }

  if (title.includes('limit reached')) {
    return 'yellow.60';
  }

  return 'neutral.90';
};

export interface ActivityListProps {
  limit: number;
  next?: string;
  autoLoad?: boolean;
  showMoreButton?: boolean;
}

const ActivityList = ({ limit, next, autoLoad = true, showMoreButton = false }: ActivityListProps) => {
  const [loaded, setLoaded] = useState(autoLoad);
  const { data: activityResponse, isLoading } = useLatestActivities(loaded, limit, next);

  if (!loaded) {
    return (
      <Button
        key="show-all-notifications"
        variant="tertiary"
        rightIconName="ArrowRight"
        size="sm"
        marginTop="8"
        marginLeft="36px"
        onClick={() => setLoaded(true)}
      >
        Load more
      </Button>
    );
  }

  return (
    <>
      <Box display="flex" flexDirection="column">
        {isLoading &&
          [...Array(limit)]
            .map((_, index) => `loading-${index}`)
            .map((key) => (
              <Skeleton key={key} isActive overflow="hidden" paddingY="8" paddingX="24">
                <SkeletonBox height="24px" width="80%" marginBottom="4" />
                <SkeletonBox height="16px" width="70%" marginBottom="4" />
              </Skeleton>
            ))}
        {activityResponse?.data.map((event) => {
          return (
            <Link
              key={event.slug}
              href={event.targetPathString}
              _hover={{ backgroundColor: 'neutral.95' }}
              paddingY="8"
              paddingX="24"
              display="flex"
              gap="16"
            >
              <Dot size={8} backgroundColor={getActivityColor(event.title)} marginY="8" flexShrink="0" />
              <Box>
                <Text textColor="neutral.20" fontSize="3" overflowWrap="anywhere">
                  {event.description}
                </Text>
                <Text textColor="neutral.40" fontSize="2">
                  <time dateTime={event.createdAt} style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {formatDateTime(event.createdAt, { hideYear: true, hideSeconds: true })}
                  </time>
                </Text>
              </Box>
            </Link>
          );
        })}
      </Box>
      {showMoreButton && activityResponse?.paging.next && (
        <ActivityList
          limit={limit}
          next={activityResponse?.paging.next}
          showMoreButton={showMoreButton}
          autoLoad={false}
        />
      )}
    </>
  );
};

export default ActivityList;
